import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
    uploadImage,
    fetchImages,
    deleteImage,
    generateMatchcard,
} from '../services/api'
import { Badge } from './Badge'
import { Banner } from './Banner'
import { Button } from './Button'
import styles from './ImageManagement.module.scss'

interface ImageManagementProps {
    entityType: 'match' | 'player' | 'tournament' | 'vod'
    entityId: string
    defaultImageType?: string // Optional default image type (overrides entity type default)
}

interface ImageData {
    image_id: string
    url: string | null
    purpose: string | null
    width: number | null
    height: number | null
    mime_type: string | null
    aspect_ratio: string | null
    created_at: string
    created_by: string | null
    type_code: string
    is_inherited?: boolean
    inherited_from_type?: string | null
    inherited_source?: {
        id: string
        type: string
        name: string | null
    } | null
}

interface UploadFormData {
    file: File | null
    title: string
    caption: string
    alt_text: string
    image_type: string
}

interface ValidationErrors {
    file?: string
    image_type?: string
    entity_id?: string
    [key: string]: string | undefined
}

export const ImageManagement: React.FC<ImageManagementProps> = ({ entityType, entityId, defaultImageType }) => {
    const [images, setImages] = useState<ImageData[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [errors, setErrors] = useState<ValidationErrors>({})
    const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [showUploadForm, setShowUploadForm] = useState(false)
    const [isGenerating, setIsGenerating] = useState(false)
    const pollAbortRef = useRef<AbortController | null>(null)

    // Map entity type to default image type
    const getDefaultImageType = (type: 'match' | 'player' | 'tournament' | 'vod'): string => {
        const typeMap = {
            match: 'match_card',
            player: 'player_headshot',
            tournament: 'competition_logo',
            vod: 'highlight_card',
        }
        return typeMap[type]
    }

    // Determine the actual default image type to use
    const actualDefaultImageType = defaultImageType || getDefaultImageType(entityType)

    // Get available image types for each entity
    const getImageTypeOptions = (type: 'match' | 'player' | 'tournament' | 'vod') => {
        const optionsMap = {
            match: [
                { value: 'match_card', label: 'Match Card' },
                { value: 'h2h', label: 'H2H' },
                { value: 'match_background', label: 'Background' },
            ],
            player: [
                { value: 'player_headshot', label: 'Headshot' },
                { value: 'player_banner', label: 'Banner' },
            ],
            tournament: [
                { value: 'competition_logo', label: 'Logo' },
                { value: 'competition_banner', label: 'Banner' },
                { value: 'competition_card', label: 'Card' },
            ],
            vod: [
                { value: 'vod_card', label: 'VOD Card' },
                { value: 'highlight_card', label: 'Highlight Card' },
                { value: 'condensed_card', label: 'Condensed Card' },
            ],
        }
        return optionsMap[type]
    }

    const [formData, setFormData] = useState<UploadFormData>({
        file: null,
        title: '',
        caption: '',
        alt_text: '',
        image_type: actualDefaultImageType,
    })

    const loadImages = useCallback(async () => {
        setIsLoading(true)
        setLoadError(null)
        try {
            const response = await fetchImages(entityType, entityId)
            setImages(response.images || [])
        } catch (error) {
            console.error('Failed to load images:', error)
            setLoadError('Failed to load images. Please try refreshing the page.')
        } finally {
            setIsLoading(false)
        }
    }, [entityType, entityId])

    useEffect(() => {
        loadImages()
        return () => {
            pollAbortRef.current?.abort()
        }
    }, [loadImages])
    
    // Reset form when upload form is shown or defaultImageType changes
    useEffect(() => {
        if (showUploadForm) {
            setFormData({
                file: null,
                title: '',
                caption: '',
                alt_text: '',
                image_type: actualDefaultImageType,
            })
        }
    }, [showUploadForm, actualDefaultImageType])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        setFormData(prev => ({ ...prev, file }))
        // Clear file error when new file is selected
        if (file && errors.file) {
            setErrors(prev => ({ ...prev, file: undefined }))
        }
    }

    const handleInputChange = (field: keyof UploadFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        // Clear field error when user types
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }))
        }
    }

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.file) {
            setErrors({ file: 'File is required' })
            return
        }

        setIsUploading(true)
        setErrors({})
        setUploadSuccess(null)

        try {
            const metadata = {
                title: formData.title || undefined,
                caption: formData.caption || undefined,
                alt_text: formData.alt_text || undefined,
                image_type: formData.image_type || undefined,
            }

            const response = await uploadImage(entityType, entityId, formData.file, metadata)

            setUploadSuccess(`Image uploaded successfully: ${response.url}`)

            // Reset form
            setFormData({
                file: null,
                title: '',
                caption: '',
                alt_text: '',
                image_type: actualDefaultImageType,
            })

            // Reset file input
            const fileInput = document.getElementById('image-file-input') as HTMLInputElement
            if (fileInput) fileInput.value = ''

            // Reload images
            await loadImages()

            // Hide form after successful upload
            setShowUploadForm(false)
        } catch (error: any) {
            console.error('Upload error:', error)

            // Try to parse validation errors from server
            try {
                const errorData = JSON.parse(error.message)
                setErrors(errorData)
            } catch {
                // If not JSON, show generic error
                setErrors({ file: error.message || 'Upload failed' })
            }
        } finally {
            setIsUploading(false)
        }
    }

    const handleDelete = async (imageId: string) => {
        const image = images.find((img) => img.image_id === imageId)
        const isDirectMatchBackground = Boolean(
            image &&
            image.type_code === 'match_background' &&
            !image.is_inherited
        )
        const reason = prompt('Reason for deleting this image? (optional)')

        const confirmMessage = isDirectMatchBackground
            ? 'Delete overridden background? The season background will be used instead (if available).'
            : 'Are you sure you want to delete this image?'
        if (!window.confirm(confirmMessage)) {
            return
        }

        try {
            await deleteImage(entityType, entityId, imageId, reason || undefined)
            setUploadSuccess('Image deleted successfully')
            await loadImages()
        } catch (error: any) {
            setErrors({ delete: error.message || 'Failed to delete image' })
        }
    }

    const handleGenerateMatchcard = async () => {
        setIsGenerating(true)
        setErrors({})
        setUploadSuccess(null)
        try {
            await generateMatchcard(entityId)
            setUploadSuccess('Matchcard generation queued. Waiting for image...')

            // Cancel any existing poll
            pollAbortRef.current?.abort()
            const abortController = new AbortController()
            pollAbortRef.current = abortController

            // Snapshot current image IDs to detect new arrivals
            const knownIds = new Set(images.map(img => img.image_id))

            // Poll in the background — does not block UI
            const POLL_INTERVAL = 1500
            const MAX_POLLS = 20 // 30s max
            let polls = 0

            const poll = async () => {
                while (polls < MAX_POLLS) {
                    if (abortController.signal.aborted) return
                    await new Promise(r => setTimeout(r, POLL_INTERVAL))
                    if (abortController.signal.aborted) return

                    polls++
                    try {
                        const response = await fetchImages(entityType, entityId)
                        const freshImages: ImageData[] = response.images || []
                        const hasNew = freshImages.some(img => !knownIds.has(img.image_id))
                        if (hasNew) {
                            setImages(freshImages)
                            setUploadSuccess('Matchcard generated successfully.')
                            setIsGenerating(false)
                            return
                        }
                    } catch {
                        // Silently ignore fetch errors during polling
                    }
                }
                // Timed out — do one final refresh and stop
                setUploadSuccess('Matchcard may still be processing. Refresh to check.')
                setIsGenerating(false)
                loadImages()
            }

            poll()
        } catch (error: any) {
            setErrors({ file: error.message || 'Failed to generate matchcard' })
            setIsGenerating(false)
        }
    }

    const closeUploadForm = () => {
        setShowUploadForm(false)
    }

    return (
        <div className={styles.imageManagement}>
            {uploadSuccess && (
                <Banner
                    variant="success"
                    message={uploadSuccess}
                    onDismiss={() => setUploadSuccess(null)}
                />
            )}

            {errors.delete && (
                <Banner
                    variant="error"
                    message={errors.delete}
                    onDismiss={() => setErrors({})}
                />
            )}

            {loadError && (
                <Banner
                    variant="error"
                    message={loadError}
                    action={
                        <Button variant="secondary" onClick={loadImages} size="small">
                            Retry
                        </Button>
                    }
                />
            )}

            {showUploadForm ? (
                <form onSubmit={handleUpload} className={styles.uploadForm}>
                    <h3>Upload New Image</h3>

                    {/* Display validation errors from server */}
                    {Object.keys(errors).filter(key => !['file', 'image_type', 'delete'].includes(key)).length > 0 && (
                        <Banner
                            variant="error"
                            message={
                                Object.entries(errors)
                                    .filter(([key]) => !['file', 'image_type', 'delete'].includes(key))
                                    .map(([, value]) => value)
                                    .join('. ')
                            }
                            onDismiss={() => setErrors(prev => {
                                const newErrors = { ...prev }
                                Object.keys(newErrors).forEach(key => {
                                    if (!['file', 'image_type', 'delete'].includes(key)) {
                                        delete newErrors[key]
                                    }
                                })
                                return newErrors
                            })}
                        />
                    )}

                    <div className={`formGrid ${styles.formGrid}`}>
                        <div className="fieldGroup">
                            <label htmlFor="image-file-input">
                                Image File *
                                {errors.file && <span className="errorText"> - {errors.file}</span>}
                            </label>
                            <input
                                id="image-file-input"
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                disabled={isUploading}
                                className={errors.file ? 'inputError' : ''}
                            />
                        </div>

                        <div className="fieldGroup">
                            <label htmlFor="image_type">
                                Image Type
                                {errors.image_type && <span className="errorText"> - {errors.image_type}</span>}
                            </label>
                            <select
                                id="image_type"
                                value={formData.image_type}
                                onChange={(e) => handleInputChange('image_type', e.target.value)}
                                disabled={isUploading}
                                className={errors.image_type ? 'inputError' : ''}
                            >
                                {getImageTypeOptions(entityType).map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="fieldGroup">
                            <label htmlFor="title">Title (optional)</label>
                            <input
                                id="title"
                                type="text"
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                disabled={isUploading}
                                placeholder="Image title"
                            />
                        </div>

                        <div className="fieldGroup">
                            <label htmlFor="alt_text">Alt Text (optional)</label>
                            <input
                                id="alt_text"
                                type="text"
                                value={formData.alt_text}
                                onChange={(e) => handleInputChange('alt_text', e.target.value)}
                                disabled={isUploading}
                                placeholder="Accessibility description"
                            />
                        </div>

                        <div className="fieldGroup">
                            <label htmlFor="caption">Caption (optional)</label>
                            <textarea
                                id="caption"
                                value={formData.caption}
                                onChange={(e) => handleInputChange('caption', e.target.value)}
                                disabled={isUploading}
                                placeholder="Image caption"
                                rows={2}
                            />
                        </div>
                    </div>

                    <div className={`formActions ${styles.formActions}`}>
                        <Button type="submit" disabled={isUploading}>
                            {isUploading ? 'Uploading...' : 'Upload Image'}
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={closeUploadForm}
                            disabled={isUploading}
                        >
                            Cancel
                        </Button>
                    </div>
                </form>
            ) : (
                <div className={styles.header}>
                    <Button
                        onClick={() => {
                            setShowUploadForm(true)
                        }}
                        variant="secondary"
                        disabled={isUploading}
                    >
                        + Upload Image
                    </Button>
                    {entityType === 'match' && (
                        <Button
                            onClick={handleGenerateMatchcard}
                            variant="secondary"
                            disabled={isGenerating}
                        >
                            {isGenerating ? 'Generating...' : 'Generate Matchcard'}
                        </Button>
                    )}
                </div>
            )}

            {isLoading ? (
                <div className={styles.loading}>Loading images...</div>
            ) : loadError ? (
                <div className={styles.emptyState}>
                    <p>Unable to load images. Please try again.</p>
                </div>
            ) : images.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No images uploaded yet.</p>
                    <p className={styles.hint}>Upload an image to get started.</p>
                </div>
            ) : (
                <div className={styles.imageGrid}>
                    {images.map((image) => (
                        <div key={image.image_id} className={styles.imageCard}>
                            <div className={styles.imagePreview}>
                                {image.url ? (
                                    <img src={image.url} alt={image.type_code} />
                                ) : (
                                    <div className={styles.imagePlaceholder}>
                                        No image URL available
                                    </div>
                                )}
                            </div>
                            <div className={styles.imageInfo}>
                                <div className={styles.imageMeta}>
                                    <h4>{image.type_code}</h4>
                                    <span className={styles.dimensions}>
                                        {image.width && image.height
                                            ? `${image.width} × ${image.height}`
                                            : image.width
                                              ? `${image.width} × ?`
                                              : image.height
                                                ? `? × ${image.height}`
                                                : 'Dimensions unknown'}
                                        {image.aspect_ratio && ` (${image.aspect_ratio})`}
                                    </span>
                                </div>
                                <div className={styles.imageDetails}>
                                    <Badge variant="secondary">{image.type_code}</Badge>
                                    {image.is_inherited ? (
                                        <Badge variant="neutral">Inherited</Badge>
                                    ) : image.type_code === 'match_background' ? (
                                        <Badge variant="neutral">Manual upload</Badge>
                                    ) : null}
                                    {image.mime_type && (
                                        <Badge variant="neutral">{image.mime_type}</Badge>
                                    )}
                                </div>
                                {image.is_inherited && image.inherited_source?.name && (
                                    <div className={styles.inheritedSource}>
                                        From: {image.inherited_source.name}
                                    </div>
                                )}
                                <div className={styles.imageFooter}>
                                    <span className={styles.uploadedBy}>
                                        {image.created_by || 'Unknown'}
                                    </span>
                                    <span className={styles.uploadedAt}>
                                        {image.created_at
                                            ? new Date(image.created_at).toLocaleDateString()
                                            : 'Unknown date'}
                                    </span>
                                </div>
                                <div className={styles.imageActions}>
                                    <Button
                                        variant="secondary"
                                        onClick={() => image.url && window.open(image.url, '_blank')}
                                        size="small"
                                        disabled={!image.url}
                                    >
                                        View
                                    </Button>
                                    {image.is_inherited ? null : (
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleDelete(image.image_id)}
                                            size="small"
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
